import { useEffect, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Activity,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Circle,
  RefreshCw,
  Ban,
  StopCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useGetGnreProgressQuery, useCancelGnreJobMutation } from "@/services/taxt-integration";
import { GnreJobStatus } from "@/@types/gnre-job-progress";
import type { GnreJobProgressDto, GnreJobStep } from "@/@types/gnre-job-progress";

const POLLING_INTERVAL = 3_000;

function StepIcon({ status }: { status: GnreJobStep["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
    case "running":
      return <Loader2 className="h-4 w-4 text-[#422534] animate-spin shrink-0" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    default:
      return <Circle className="h-4 w-4 text-gray-300 shrink-0" />;
  }
}

function formatTime(isoString?: string) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleTimeString("pt-BR");
}

function getElapsedTime(startTime?: string) {
  if (!startTime) return "";
  const diff = Date.now() - new Date(startTime).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0)
    return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
  return `${seconds}s`;
}

function JobStatusBadge({ status }: { status: string }) {
  const config = {
    RUNNING: { label: "Processando", className: "bg-[#422534]/10 text-[#422534]" },
    COMPLETED: { label: "Concluído", className: "bg-green-100 text-green-700" },
    FAILED: { label: "Falhou", className: "bg-red-100 text-red-700" },
    CANCELLED: { label: "Cancelado", className: "bg-orange-100 text-orange-700" },
  }[status] || { label: status, className: "bg-gray-100 text-gray-600" };

  return (
    <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap", config.className)}>
      {config.label}
    </span>
  );
}

function JobCard({ job, onCancel, isCancelling }: {
  job: GnreJobProgressDto;
  onCancel: (jobId: string) => void;
  isCancelling: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const isRunning = job.status === GnreJobStatus.RUNNING;
  const isFailed = job.status === GnreJobStatus.FAILED;
  const isCancelled = job.status === GnreJobStatus.CANCELLED;

  return (
    <div
      className={cn(
        "rounded-lg border transition-colors",
        isRunning && "border-[#422534]/30 bg-[#422534]/[0.02]",
        isFailed && "border-red-200",
        isCancelled && "border-orange-200 bg-orange-50/30",
        !isRunning && !isFailed && !isCancelled && "border-gray-200",
      )}
    >
      {/* Job header */}
      <div
        className="flex items-center justify-between p-3 gap-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {isRunning ? (
            <Loader2 className="h-4 w-4 text-[#422534] animate-spin shrink-0" />
          ) : job.status === GnreJobStatus.COMPLETED ? (
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          ) : isCancelled ? (
            <Ban className="h-4 w-4 text-orange-500 shrink-0" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700 truncate">
                {job.initiatedBy?.userEmail || "Sistema"}
              </span>
              <JobStatusBadge status={job.status} />
            </div>
            <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
              <span>{formatTime(job.startTime)}</span>
              <span className="flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" />
                {getElapsedTime(job.startTime)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isRunning && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-3 text-[11px] border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmCancel(true);
              }}
            >
              <StopCircle className="h-3 w-3 mr-1" />
              Cancelar
            </Button>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Cancel confirmation */}
      {confirmCancel && isRunning && (
        <div className="mx-3 mb-3 p-2.5 rounded-md border border-red-200 bg-red-50 flex items-center justify-between gap-2">
          <span className="text-xs text-red-700">
            Tem certeza? O processamento será interrompido.
          </span>
          <div className="flex gap-1.5 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px]"
              onClick={() => setConfirmCancel(false)}
            >
              Não
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="h-6 px-3 text-[10px]"
              disabled={isCancelling}
              onClick={() => {
                onCancel(job.jobId);
                setConfirmCancel(false);
              }}
            >
              {isCancelling ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Sim, cancelar"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Steps */}
      {expanded && (
      <div className="px-3 pb-3 space-y-1 border-t border-gray-100 pt-2">
        {job.steps.map((step, index) => (
          <div
            key={index}
            className={cn(
              "flex items-start gap-2.5 p-2 rounded-md transition-colors",
              step.status === "running" && "bg-[#422534]/5",
              step.status === "failed" && "bg-red-50",
              step.status === "pending" && "opacity-40",
            )}
          >
            <div className="flex flex-col items-center gap-1 pt-0.5">
              <StepIcon status={step.status} />
              {index < job.steps.length - 1 && (
                <div
                  className={cn(
                    "w-px h-3",
                    step.status === "completed" ? "bg-green-300" : "bg-gray-200"
                  )}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={cn(
                  "text-xs font-medium",
                  step.status === "pending" && "text-gray-400",
                  step.status === "running" && "text-[#422534]",
                  step.status === "completed" && "text-gray-600",
                  step.status === "failed" && "text-red-600"
                )}
              >
                {step.name}
              </span>
              {step.message && (
                <p
                  className={cn(
                    "text-[10px] mt-0.5",
                    step.status === "failed" ? "text-red-500" : "text-gray-400"
                  )}
                >
                  {step.message}
                </p>
              )}
            </div>
          </div>
        ))}

        {(isFailed || isCancelled) && job.error && (
          <div className={cn(
            "rounded-md border p-2.5 mt-1",
            isCancelled ? "border-orange-200 bg-orange-50" : "border-red-200 bg-red-50"
          )}>
            <p className={cn(
              "text-[11px]",
              isCancelled ? "text-orange-600" : "text-red-600"
            )}>
              {job.error}
            </p>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export function GnreProgressModal() {
  const [open, setOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data, refetch, isFetching } = useGetGnreProgressQuery(undefined, {
    skip: !open,
  });

  const [cancelJob, { isLoading: isCancelling }] = useCancelGnreJobMutation();

  const jobs = data?.jobs || [];
  const hasRunning = jobs.some((j) => j.status === GnreJobStatus.RUNNING);
  const runningCount = jobs.filter((j) => j.status === GnreJobStatus.RUNNING).length;

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (open && hasRunning) {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          refetchRef.current();
        }, POLLING_INTERVAL);
      }
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, hasRunning]);

  const handleOpen = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) refetch();
    },
    [refetch]
  );

  const handleCancel = useCallback(
    async (jobId: string) => {
      try {
        await cancelJob(jobId).unwrap();
        toast({
          variant: "default",
          description: "Job cancelado com sucesso.",
          duration: 3000,
        });
        refetch();
      } catch {
        toast({
          variant: "destructive",
          description: "Não foi possível cancelar o job.",
          duration: 4000,
        });
      }
    },
    [cancelJob, refetch]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2 h-9",
            hasRunning && "border-[#422534] text-[#422534]"
          )}
        >
          <Activity className={cn("h-4 w-4", hasRunning && "animate-pulse")} />
          Status GNRE
          {runningCount > 0 && (
            <span className="flex items-center gap-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#422534] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#422534]" />
              </span>
              {runningCount > 1 && (
                <span className="text-[10px] font-bold">{runningCount}</span>
              )}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader className="pr-8">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Progresso GNRE
              {runningCount > 0 && (
                <span className="text-xs font-normal text-gray-500">
                  ({runningCount}/3 em execução)
                </span>
              )}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                className={cn("h-3 w-3 mr-1", isFetching && "animate-spin")}
              />
              Atualizar
            </Button>
          </div>
        </DialogHeader>

        {jobs.length > 0 ? (
          <ScrollArea className="max-h-[70vh] pr-1">
            <div className="space-y-3">
              {jobs.map((job) => (
                <JobCard
                  key={job.jobId}
                  job={job}
                  onCancel={handleCancel}
                  isCancelling={isCancelling}
                />
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Activity className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Nenhum processamento GNRE recente</p>
            <p className="text-xs mt-1">
              Inicie uma geração de GNRE para acompanhar o progresso aqui
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
